import Link from '@tiptap/extension-link';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { useEffect } from 'react';

function ToolbarButton({
  icon: Icon,
  title,
  active,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`rounded p-1.5 transition-colors ${
        active ? 'bg-brand-accent/10 text-brand-accent' : 'text-slate-500 hover:bg-slate-100'
      }`}
    >
      <Icon size={15} />
    </button>
  );
}

/** Minimaler WYSIWYG-Editor (Tiptap/ProseMirror) für frei formatierbare
 * Rechtstexte (Plattform › Impressum & Datenschutz, `PlatformLegalPage.tsx`)
 * — Fett/Kursiv, zwei Überschriftenebenen, Listen, Zitat, Links,
 * Rückgängig/Wiederholen. Arbeitet direkt mit HTML (`editor.getHTML()`),
 * keine Zwischenformate — dasselbe HTML landet unverändert in
 * `platformSettings.legal` und wird auf den öffentlichen Seiten
 * (`ImprintPage.tsx`/`PrivacyPage.tsx`) nach DOMPurify-Sanitisierung
 * gerendert. Layout/Typografie des Ergebnisses: `.legal-content` in
 * `index.css`, geteilt zwischen Editor und öffentlicher Ansicht, damit die
 * Vorschau beim Bearbeiten genau dem späteren Live-Ergebnis entspricht. */
export function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: 'legal-content min-h-[280px] focus:outline-none' } },
  });

  // Tiptap only tracks edits made through its own commands — externally
  // changed `value` (initial data arriving from the query, or the "Auf
  // Standard zurücksetzen"-Button below) must be pushed in explicitly.
  // `emitUpdate: false` avoids an infinite loop back through `onChange`.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? 'https://';
    const url = window.prompt('Link-Adresse (leer lassen zum Entfernen):', previousUrl);
    if (url === null) return;
    const chain = editor.chain().focus().extendMarkRange('link');
    if (url === '') chain.unsetLink().run();
    else chain.setLink({ href: url }).run();
  };

  return (
    <div className="overflow-hidden rounded-md border border-slate-300 bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-1.5">
        <ToolbarButton
          icon={Bold}
          title="Fett"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={Italic}
          title="Kursiv"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton
          icon={Heading2}
          title="Überschrift"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <ToolbarButton
          icon={Heading3}
          title="Unterüberschrift"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton
          icon={List}
          title="Aufzählung"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon={ListOrdered}
          title="Nummerierte Liste"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          icon={Quote}
          title="Zitat"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton icon={Link2} title="Link" active={editor.isActive('link')} onClick={setLink} />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton icon={Undo2} title="Rückgängig" onClick={() => editor.chain().focus().undo().run()} />
        <ToolbarButton icon={Redo2} title="Wiederholen" onClick={() => editor.chain().focus().redo().run()} />
      </div>
      <EditorContent editor={editor} className="max-h-[500px] overflow-y-auto px-4 py-3 text-sm" />
    </div>
  );
}
